import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import ErrorIcon from '@material-ui/icons/Error';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { MILIEUS, milieuImageUrl } from './milieus';
import { postInRoom } from './inRoomApi';
import customApplicationConfiguration from '../../Custom/CustomApplicationConfiguration';
import CustomProgress from '../CustomProgress/CustomProgress';
import { black, errorRed, white } from '../../Constants/Colors';

const useStyles = makeStyles((theme) => ({
  dialogContentRoot: { padding: '0 24px 24px 12px' },
  intro: { fontSize: '1.25rem', fontWeight: 400, margin: '4px 0 16px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  tile: {
    cursor: 'pointer',
    border: '2px solid transparent',
    borderRadius: 4,
    overflow: 'hidden',
    padding: 0,
    background: 'none',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)' },
  },
  tileSelected: { borderColor: theme.palette.primary.main },
  thumb: { display: 'block', width: '100%', height: 'auto' },
  name: { fontSize: 13, fontWeight: 500, textAlign: 'center', padding: '6px 0' },
  loader: { padding: 32, textAlign: 'center' },
  // LoaderIcon (Components/CustomProgress/Loader.tsx) is a fixed 161x161px SVG; CustomProgress
  // positions it absolutely, so this wrapper must be at least that tall or it overflows into
  // whatever follows it.
  loaderIconWrap: { position: 'relative', height: 180, marginBottom: 12 },
  result: { marginTop: 8 },
  resultImg: { display: 'block', width: '100%', height: 'auto', borderRadius: 4 },
  error: {
    background: errorRed,
    color: white,
    padding: '14px 8px',
    borderRadius: 4,
    fontSize: 14,
    lineHeight: 1.7,
    textAlign: 'center',
    marginTop: 16,
  },
  errorIcon: { float: 'left' },
  download: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    width: 'auto',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 20px',
    border: 'none',
    backgroundColor: theme.palette.primary.main,
    color: white,
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.02857em',
    borderRadius: 0,
    '&:hover': { backgroundColor: black },
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
}

const InRoomView: React.FC<Props> = ({ open, onClose }) => {
  const classes = useStyles();
  const baseUrl = customApplicationConfiguration.network.baseURL;
  const imageUrl = customApplicationConfiguration.network.imageURL;

  const [selected, setSelected] = useState<string | null>(null);
  const [phase, setPhase] = useState('pick'); // 'pick' | 'loading' | 'result' | 'error'
  const [image, setImage] = useState('');
  const [errorKey, setErrorKey] = useState('');

  const milieus = useMemo(() => MILIEUS, []);

  useEffect(() => {
    if (open) {
      setPhase('pick');
      setSelected(null);
      setImage('');
      setErrorKey('');
    }
  }, [open]);

  const downloadName = () => {
    const mime = (image.match(/^data:image\/([a-z0-9.+-]+);/i) || [])[1] || 'png';
    const ext = mime === 'jpeg' ? 'jpg' : mime;
    return `bett-im-raum-${selected}.${ext}`;
  };

  // Capture the current bed straight from the live 3D canvas (models loaded from prod) as a data URL —
  // no save, no dialogs. getScreenshots renders every configured view; we take the milieu's bedView.
  const captureBed = (view: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const component =
        Services.visualization && Services.visualization.getComponent && Services.visualization.getComponent();
      if (!component || typeof component.getScreenshots !== 'function') {
        reject(new Error('inRoom.error.generic'));
        return;
      }
      const variant = component._materialVariantPath || '';
      const done = (shots: any) => {
        const views = shots && Object.values(shots)[0];
        const dataUrl = views && (views as any)[view];
        if (dataUrl) resolve(dataUrl);
        else reject(new Error('inRoom.error.blankRender'));
      };
      // High-res capture: the bed is dominance-cropped server-side, so more pixels here = more real
      // detail for Gemini to preserve instead of inventing.
      const maybePromise = component.getScreenshots(done, { variantList: [variant], size: { width: 2560, height: 1920 } });
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch(() => reject(new Error('inRoom.error.generic')));
      }
    });

  const generate = async () => {
    setPhase('loading');
    setErrorKey('');
    try {
      const milieu = milieus.find((m) => m.slug === selected);
      const bedImage = await captureBed((milieu && milieu.bedView) || 'default');
      const { image: img } = await postInRoom({ baseUrl, bedImage, milieu: selected });
      setImage(img);
      setPhase('result');
    } catch (e) {
      const key = typeof e?.message === 'string' && e.message.startsWith('inRoom.') ? e.message : 'inRoom.error.generic';
      setErrorKey(key);
      setPhase('error');
    }
  };

  // A generation can take ~20-60s and is not free to re-run; an accidental backdrop click
  // must not silently discard it. Only the explicit X/cancel action (onCancel) closes the dialog.
  const handleDialogClose = (event?: unknown, reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleDialogClose}
      onCancel={onClose}
      dialogTitle={t('inRoom.dialogTitle')}
      maxWidth="lg"
      fullWidth
    >
      <DialogContent className={classes.dialogContentRoot}>
        {phase !== 'loading' && <div className={classes.intro}>{t('inRoom.intro')}</div>}

        {(phase === 'pick' || phase === 'error') && (
          <>
            <div className={classes.grid}>
              {milieus.map((m) => (
                <button
                  key={m.slug}
                  type="button"
                  className={`${classes.tile} ${selected === m.slug ? classes.tileSelected : ''}`}
                  onClick={() => setSelected(m.slug)}
                >
                  <img className={classes.thumb} src={milieuImageUrl(imageUrl, m.thumb)} alt={m.name} loading="lazy" />
                  <div className={classes.name}>{m.name}</div>
                </button>
              ))}
            </div>
            {phase === 'error' && (
              <div className={classes.error}>
                <ErrorIcon className={classes.errorIcon} />
                {t(errorKey)}
              </div>
            )}
          </>
        )}

        {phase === 'loading' && (
          <div className={classes.loader}>
            <div className={classes.loaderIconWrap}>
              <CustomProgress loading />
            </div>
            {t('inRoom.loading')}
          </div>
        )}

        {phase === 'result' && (
          <div className={classes.result}>
            <img className={classes.resultImg} src={image} alt={t('inRoom.dialogTitle')} />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {(phase === 'pick' || phase === 'error') && (
          <MainButton onClick={generate} variant="contained" size="medium" disabled={!selected}>
            {t('inRoom.generate')}
          </MainButton>
        )}
        {phase === 'result' && (
          <a className={classes.download} href={image} download={downloadName()}>
            {t('inRoom.download')}
          </a>
        )}
      </DialogActions>
    </CustomDialog>
  );
};

export default InRoomView;
